import {AxiosError, AxiosResponse} from 'axios';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';

import {setEntities} from '@ngneat/elf-entities';
import {getAuditsInformationDataSource} from './audit-information.requests';
import {
  AuditInformation,
  AuditInformationContactModel,
  AuditInformationCreatedFromStepEnum,
  AuditInformationDataModel,
  AuditInformationMapModel,
  AuditInformationNeedModel,
  AuditInformationObservationModel,
  RepositionDTO,
  SearchAuditInformationDto
} from './audits-information.model';
import {auditsInformationStore} from './audits-information.store';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class AuditsInformationService {
  store = auditsInformationStore;

  resetStore = () => this.store.reset();

  getStep = () => {
    const {step} = this.store.getValue();
    return step;
  };
  setStep = (step: AuditInformationCreatedFromStepEnum) =>
    this.store.update((state) => ({
      ...state,
      step: step
    }));


  getAuditsInformation = (auditId: number | string): Observable<AuditInformation[]> => {
    const searchDto: SearchAuditInformationDto = {step: this.getStep()};

    return from(
      APIAxios({
        ...APIRoutes.GETAuditsInformation(auditId),
        params: {
          createdFrom: searchDto.step
        }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<AuditInformation[]>) => {
        return response.data;
      }),
      tap((auditsInformation) => {

        this.store.update(
          setEntities(auditsInformation),
          getAuditsInformationDataSource.setSuccess()
        );
      }),
    );
  };

  createAuditInformationContact = (data: AuditInformationContactModel) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsInformationContact(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  updateAuditInformationContact = (data: AuditInformationContactModel, auditInformation: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationContact(auditInformation),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  createAuditInformationNeed = (data: AuditInformationNeedModel) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsInformationNeed(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  updateAuditInformationNeed = (data: AuditInformationNeedModel, auditInformation: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationNeed(auditInformation),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  createAuditInformationData = (data: AuditInformationDataModel) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsInformationData(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  updateAuditInformationData = (data: AuditInformationDataModel, auditInformation: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationData(auditInformation),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  createAuditInformationMap = (data: AuditInformationMapModel) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsInformationMap(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  updateAuditInformationMap = (data: AuditInformationMapModel, auditInformation: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationMap(auditInformation),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  createAuditInformationObservation = (data: AuditInformationObservationModel) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsInformationObservation(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  updateAuditInformationObservation = (data: AuditInformationObservationModel, auditInformation: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationObservation(auditInformation),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditInformation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformation(data.auditId!)),
      );
  };

  deleteAuditInformation = (auditInformationId: number | string, auditId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEAuditsInformation(auditInformationId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAuditsInformation(auditId)),
      );
  };

  updatePosition = (data: RepositionDTO, auditId: number | string) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsInformationPosition(),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAuditsInformation(auditId)),
      );
  };

  getPreAuditCSV = (auditId: string | number) => {
    return from(
      APIAxios({
        ...APIRoutes.GETPreAuditCSV(auditId)
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      tap((response: AxiosResponse<Blob>) => {
        const filename = extractFilenameFromResponse(response);
        filesService.downloadDocument(response.data, filename || 'export.xlsx');
      }),
    );
  };

}

export const auditsInformationService = new AuditsInformationService();
